/**
 * Background Canvas Renderer
 * 
 * Separate canvas renderer for background images and effects only.
 * This enables shader effects on the background without affecting other canvas renderings.
 */

import type { GridState } from '../types/square.js';
import { drawBackground, type BackgroundState } from './background.js';

/**
 * Background canvas renderer
 */
export class BackgroundCanvasRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr: number;
  private dirty: boolean;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get 2D context');
    this.ctx = ctx;
    this.dpr = Math.min(2, window.devicePixelRatio || 1);
    this.dirty = true;
  }

  /**
   * Mark canvas as dirty (needs redraw)
   */
  markDirty(): void {
    this.dirty = true;
  }

  /**
   * Render background to canvas
   */
  render(
    gridState: GridState,
    bgState: BackgroundState
  ): void {
    // Update canvas size if needed
    const newWidth = Math.floor(gridState.width * this.dpr);
    const newHeight = Math.floor(gridState.height * this.dpr);

    if (this.dirty || this.canvas.width !== newWidth || this.canvas.height !== newHeight) {
      this.canvas.width = newWidth;
      this.canvas.height = newHeight;
      this.canvas.style.width = gridState.width + 'px';
      this.canvas.style.height = gridState.height + 'px';
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.dirty = false;
    }

    // For drawing mode, don't clear - preserve existing drawings
    // Just draw a light marble background underneath
    if (bgState.type === 'drawing') {
      // Draw light marble background for drawing mode (don't clear existing drawings)
      // Create marble-like gradient (slightly darker)
      const marbleGradient = this.ctx.createLinearGradient(0, 0, gridState.width, gridState.height);
      marbleGradient.addColorStop(0, '#e8e6e3');
      marbleGradient.addColorStop(0.3, '#edeae7');
      marbleGradient.addColorStop(0.5, '#e0ddd8');
      marbleGradient.addColorStop(0.7, '#edeae7');
      marbleGradient.addColorStop(1, '#e8e6e3');
      this.ctx.fillStyle = marbleGradient;
      this.ctx.fillRect(0, 0, gridState.width, gridState.height);
      
      // Add subtle marble texture with noise
      this.ctx.globalAlpha = 0.15;
      for (let i = 0; i < 50; i++) {
        const x = Math.random() * gridState.width;
        const y = Math.random() * gridState.height;
        const radius = 20 + Math.random() * 40;
        const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, 'rgba(200, 195, 185, 0.3)');
        gradient.addColorStop(1, 'rgba(200, 195, 185, 0)');
        this.ctx.fillStyle = gradient;
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, Math.PI * 2);
        this.ctx.fill();
      }
      this.ctx.globalAlpha = 1.0;
    } else if (bgState.type === 'paper') {
      // Clear canvas for paper mode
      this.ctx.clearRect(0, 0, gridState.width, gridState.height);
      
      // Create marble gradient (diagonal from top-left to bottom-right)
      const marbleGradient = this.ctx.createLinearGradient(0, 0, gridState.width, gridState.height);
      marbleGradient.addColorStop(0, '#e8e6e3');      // Start: light beige
      marbleGradient.addColorStop(0.3, '#edeae7');    // 30%: lighter beige
      marbleGradient.addColorStop(0.5, '#e0ddd8');    // 50%: darker beige (center)
      marbleGradient.addColorStop(0.7, '#edeae7');    // 70%: lighter beige
      marbleGradient.addColorStop(1, '#e8e6e3');      // End: light beige
      
      // Apply gradient
      this.ctx.fillStyle = marbleGradient;
      this.ctx.fillRect(0, 0, gridState.width, gridState.height);
      
      // Add marble texture with noise overlay
      this.ctx.globalAlpha = 0.15;  // 15% opacity for subtle texture
      for (let i = 0; i < 50; i++) {
        const x = Math.random() * gridState.width;
        const y = Math.random() * gridState.height;
        const radius = 20 + Math.random() * 40;  // Radius between 20-60px
        
        // Create radial gradient for each noise spot
        const gradient = this.ctx.createRadialGradient(x, y, 0, x, y, radius);
        gradient.addColorStop(0, 'rgba(200, 195, 185, 0.3)');  // Center: semi-transparent beige
        gradient.addColorStop(1, 'rgba(200, 195, 185, 0)');    // Edge: fully transparent
        
        this.ctx.fillStyle = gradient;
        this.ctx.beginPath();
        this.ctx.arc(x, y, radius, 0, Math.PI * 2);
        this.ctx.fill();
      }
      this.ctx.globalAlpha = 1.0;  // Reset opacity
    } else {
      // Clear canvas for other background types
      this.ctx.clearRect(0, 0, gridState.width, gridState.height);
      
      // Draw background only
      drawBackground(this.ctx, bgState, gridState.width, gridState.height);
    }
  }
}

